import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronRight } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cashflow-596/artifacts/ipba3h4w_Gazeal%20Advance%20logo.png";
const POWERED_BY_LOGO_URL = "/powered-by-agents-capital.png";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const navLinks = [
    { name: 'How It Works', href: '/#how-it-works' },
    { name: 'Why Gazeal', href: '/#why-gazeal' },
    { name: 'FAQ', href: '/#faq' },
  ];

  const scrollToSection = (e, href) => {
    if (href.startsWith('/#')) {
      e.preventDefault();
      const sectionId = href.substring(2);
      
      // Close mobile menu immediately
      setIsMobileMenuOpen(false);
      
      // Check if we're on the homepage
      if (location.pathname === '/') {
        // Already on homepage, just scroll
        const element = document.getElementById(sectionId);
        if (element) {
          const headerOffset = 100;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      } else {
        // Not on homepage, navigate first then scroll
        navigate('/');
        // Wait for navigation to complete, then scroll
        setTimeout(() => {
          const element = document.getElementById(sectionId);
          if (element) {
            const headerOffset = 100;
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
            
            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            });
          }
        }, 100);
      }
    }
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ${
          isScrolled ? 'py-3' : 'py-4'
        }`}
      >
        <div className={`max-w-[1400px] mx-auto px-4 lg:px-8 transition-all duration-700 ${
          isScrolled ? 'px-4' : 'px-6 lg:px-12'
        }`}>
          {/* Liquid Glass Container */}
          <div
            className={`relative transition-all duration-700 ease-out ${
              isScrolled
                ? 'bg-white/[0.03] backdrop-blur-2xl border border-white/[0.08] shadow-[0_8px_32px_rgba(0,0,0,0.4),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-full px-6 py-2'
                : 'bg-transparent px-2 py-2'
            }`}
            style={{
              ...(isScrolled && {
                background: 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 50%, rgba(0,0,0,0.1) 100%)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.1), inset 0 -1px 0 rgba(0,0,0,0.1)',
              })
            }}
          >
            {/* Inner glow effect when scrolled */}
            {isScrolled && (
              <div className="absolute inset-0 rounded-full overflow-hidden pointer-events-none">
                <div className="absolute inset-0 bg-gradient-to-b from-white/[0.08] via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-white/10 to-transparent" />
              </div>
            )}

            <div className="relative flex items-center justify-between h-14">
              {/* Logo with Powered By */}
              <Link to="/" className="flex items-center gap-3 z-10">
                <img
                  src={LOGO_URL}
                  alt="Gazeal Advance"
                  className={`transition-all duration-500 ${isScrolled ? 'h-7 md:h-8' : 'h-8 md:h-10'} w-auto`}
                />
                <div className={`transition-all duration-500 ${isScrolled ? 'opacity-80' : 'opacity-100'}`}>
                  <img
                    src={POWERED_BY_LOGO_URL}
                    alt="Powered by AgentsCapital"
                    className={`transition-all duration-500 ${isScrolled ? 'h-5 md:h-6' : 'h-6 md:h-7'} w-auto`}
                  />
                </div>
              </Link>

              {/* Desktop Navigation */}
              <nav className="hidden lg:flex items-center gap-8">
                {navLinks.map((link) => (
                  <a
                    key={link.name}
                    href={link.href}
                    onClick={(e) => scrollToSection(e, link.href)}
                    className="text-white/60 hover:text-white transition-all duration-300 text-[14px] font-medium relative group"
                  >
                    {link.name}
                    <span className="absolute -bottom-1 left-0 w-0 h-[2px] bg-[#59d862] transition-all duration-300 group-hover:w-full" />
                  </a>
                ))}
              </nav>

              {/* CTA Buttons */}
              <div className="hidden lg:flex items-center gap-3">
                <Link
                  to="/apply"
                  className="text-white/70 hover:text-white transition-all duration-300 text-[14px] font-medium px-4 py-2"
                >
                  Apply to Join
                </Link>
                <Link
                  to="/get-paid"
                  className={`text-[14px] px-5 py-2.5 flex items-center gap-2 font-semibold transition-all duration-500 ${
                    isScrolled
                      ? 'bg-[#59d862] text-black rounded-full shadow-[0_4px_15px_rgba(89,216,98,0.3)]'
                      : 'bg-[#59d862] text-black rounded-full shadow-[0_4px_15px_rgba(89,216,98,0.25)]'
                  } hover:shadow-[0_6px_20px_rgba(89,216,98,0.4)] hover:scale-105`}
                >
                  Get Paid Now
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={`lg:hidden relative z-50 p-2 transition-all duration-300 ${
                  isMobileMenuOpen 
                    ? 'text-white' 
                    : 'text-white hover:text-[#59d862]'
                }`}
              >
                {isMobileMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu - Liquid Glass Full Screen */}
      <div
        className={`lg:hidden fixed inset-0 z-40 transition-all duration-500 ${
          isMobileMenuOpen 
            ? 'opacity-100 visible' 
            : 'opacity-0 invisible pointer-events-none'
        }`}
      >
        {/* Backdrop blur overlay */}
        <div 
          className={`absolute inset-0 transition-all duration-700 ${
            isMobileMenuOpen ? 'backdrop-blur-2xl' : 'backdrop-blur-none'
          }`}
          style={{
            background: isMobileMenuOpen 
              ? 'linear-gradient(180deg, rgba(0,0,0,0.7) 0%, rgba(0,20,25,0.95) 30%, rgba(0,0,0,0.98) 100%)'
              : 'transparent'
          }}
          onClick={() => setIsMobileMenuOpen(false)}
        />
        
        {/* Liquid Glass Menu Container */}
        <div 
          className={`absolute top-24 left-4 right-4 transition-all duration-500 ease-out ${
            isMobileMenuOpen 
              ? 'translate-y-0 opacity-100 scale-100' 
              : '-translate-y-8 opacity-0 scale-95'
          }`}
        >
          <div 
            className="relative overflow-hidden rounded-3xl"
            style={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 50%, rgba(0,0,0,0.2) 100%)',
              backdropFilter: 'blur(40px) saturate(180%)',
              WebkitBackdropFilter: 'blur(40px) saturate(180%)',
              border: '1px solid rgba(255,255,255,0.1)',
              boxShadow: '0 25px 50px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.15), inset 0 -1px 0 rgba(0,0,0,0.2)',
            }}
          >
            {/* Top highlight */}
            <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-white/20 to-transparent" />
            
            {/* Inner glow */}
            <div className="absolute inset-0 bg-gradient-to-b from-white/[0.05] via-transparent to-transparent pointer-events-none" />
            
            {/* Animated gradient orbs */}
            <div className="absolute -top-20 -right-20 w-40 h-40 bg-[#59d862]/20 rounded-full blur-3xl animate-pulse" />
            <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-[#004048]/30 rounded-full blur-3xl" />
            
            <div className="relative p-6">
              {/* Navigation Links */}
              <div className="space-y-2 mb-6">
                {navLinks.map((link, index) => (
                  <a
                    key={link.name}
                    href={link.href}
                    onClick={(e) => scrollToSection(e, link.href)}
                    className="block text-white/80 hover:text-white text-xl font-medium py-4 px-4 rounded-2xl transition-all duration-300 hover:bg-white/[0.05] border border-transparent hover:border-white/[0.08]"
                    style={{
                      transitionDelay: `${index * 50}ms`,
                      transform: isMobileMenuOpen ? 'translateX(0)' : 'translateX(-20px)',
                      opacity: isMobileMenuOpen ? 1 : 0,
                    }}
                  >
                    {link.name}
                  </a>
                ))}
              </div>
              
              {/* Divider */}
              <div className="h-[1px] bg-gradient-to-r from-transparent via-white/10 to-transparent mb-6" />
              
              {/* CTA Buttons */}
              <div className="flex flex-col gap-3">
                <Link
                  to="/apply"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-center py-4 text-lg font-medium rounded-2xl transition-all duration-300 border border-white/10 text-white/90 hover:text-white hover:bg-white/[0.05] hover:border-white/20"
                  style={{
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)',
                  }}
                >
                  Apply to Join
                </Link>
                <Link
                  to="/get-paid"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-center py-4 text-lg font-semibold rounded-2xl transition-all duration-300 bg-[#59d862] text-black hover:bg-[#6de876] shadow-[0_8px_25px_rgba(89,216,98,0.3)] hover:shadow-[0_12px_35px_rgba(89,216,98,0.4)] hover:scale-[1.02]"
                >
                  Get Paid Now
                </Link>
              </div>
            </div>
            
            {/* Bottom highlight */}
            <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-white/5 to-transparent" />
          </div>
        </div>
        
        {/* Floating accent at bottom */}
        <div 
          className={`absolute bottom-8 left-1/2 -translate-x-1/2 transition-all duration-700 delay-300 ${
            isMobileMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <div className="text-white/30 text-xs text-center">
            Swipe or tap outside to close
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
